import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import Flickity from 'react-flickity-component';
import {formatMoney} from '@shopify/theme-currency';

import {imageUrl} from '../../../../scripts/utils/shopify-images';
import iconChevronDown from '../../../../assets/icon-chevron-down.svg';
import iconChevronLeft from '../../../../assets/icon-chevron-left.svg';
import iconChevronRight from '../../../../assets/icon-chevron-right.svg';

const GiftWithPurchase = ({gifts = [], excludedGifts = [], claimedGifts = [], remainingSpend = [], addToCart}) => {
  const [giftVisibility, setGiftVisibility] = useState(true);

  useEffect(() => {
    if (!claimedGifts.length && gifts.length) {
      setGiftVisibility(true);
    }
  }, [gifts, claimedGifts]);

  const addGWPProductToCart = async ({variantId, minimumSpend}) => {
    if (!variantId) return;

    const item = {
      id: variantId,
      quantity: 1,
      properties: {
        _gwp: true,
        _gwp_minimum_spend: minimumSpend,
      },
    };
    await addToCart(item, false);
    setGiftVisibility(false);
  };

  const minRemainingSpend = remainingSpend.length ? formatMoney(Math.min(...remainingSpend)) : 0;

  const gwpHeaderTitle = () => {
    if (!claimedGifts.length && !gifts.length) {
      return window.theme.strings.giftWithPurchase.remainingSpend.replace('{{ amount }}', minRemainingSpend);
    } else if (gifts.length) {
      return window.theme.strings.giftWithPurchase.redeemPrompt;
    } else if (claimedGifts.length && minRemainingSpend) {
      return window.theme.strings.giftWithPurchase.additionalRemainingSpend.replace('{{ amount }}', minRemainingSpend);
    } else {
      return window.theme.strings.giftWithPurchase.giftsRedeemed;
    }
  };

  const flktyRef = useRef(null);

  const handlePrevious = () => {
    if (flktyRef.current) {
      flktyRef.current.previous();
    }
  };

  const handleNext = () => {
    if (flktyRef.current) {
      flktyRef.current.next();
    }
  };

  const giftSlides = gifts.map((product) => {
    const {variants, title, id, handle, price} = product;
    const [selectedVariantId, setSelectedVariantId] = useState(variants.length === 1 ? variants[0].id : '');

    useEffect(() => {
      setSelectedVariantId(variants.length === 1 ? variants[0].id : '');
    }, [product.id, variants]);

    const featuredImage = product.featured_image;

    return (
      <div key={id} className="gwp__slide">
        <div class="gwp-product">
          <a href={`/products/${handle}`} className="aspect-ratio aspect-ratio--portrait">
            {featuredImage && (
              <img
                src={imageUrl(featuredImage, 160, 240, 'center')}
                width={80}
                height={120}
                alt={title}
                loading="lazy"
              />
            )}
          </a>

          <div className="gwp-product__content">
            <h3 className="gwp-product__title h5 caption">{title}</h3>
            <div className="gwp-product__price caption">
              {window.theme.strings.giftWithPurchase.free} <span>{formatMoney(price)}</span>
            </div>
            <div className="gwp-product__variants">
              {variants.length > 1 && (
                <select
                  className="gwp-product__variant-select"
                  value={selectedVariantId}
                  onChange={(e) => setSelectedVariantId(e.target.value)}
                >
                  <option value="">{window.theme.strings.giftWithPurchase.variantSelect}</option>
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.id} disabled={!variant.available}>
                      {variant.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <button
            type="button"
            className="gwp__button"
            disabled={claimedGifts.length >= 1 || (variants.length > 1 && !selectedVariantId)}
            onClick={() =>
              addGWPProductToCart({
                variantId: selectedVariantId || variants[0].id,
                minimumSpend: product.minimum_spend,
              })
            }
          >
            {window.theme.strings.giftWithPurchase.add}
          </button>
        </div>
      </div>
    );
  });

  return (
    <div className="gwp">
      {gifts.length ? (
        <>
          <button
            type="button"
            className="btn-none gwp__banner h5 caption"
            onClick={() => setGiftVisibility(!giftVisibility)}
          >
            {gwpHeaderTitle()}
            <span aria-hidden={!giftVisibility} dangerouslySetInnerHTML={{__html: iconChevronDown}} />
          </button>
          <div
            className={`gwp__wrapper ${giftVisibility ? 'gwp__wrapper--visible' : ''}`}
            data-gift-count={gifts.length}
            aria-hidden={!giftVisibility}
          >
            {gifts.length > 1 ? (
              <div className="gwp__controls-wrapper">
                <Flickity
                  className="gwp__slider"
                  elementType="div"
                  options={{
                    resize: false,
                    cellAlign: 'center',
                    pageDots: true,
                    prevNextButtons: false,
                    contain: false,
                  }}
                  disableImagesLoaded={false}
                  static
                  flickityRef={(c) => (flktyRef.current = c)}
                >
                  {giftSlides}
                </Flickity>
                <div className="gwp__controls">
                  <button className="btn btn--previous btn-none" onClick={handlePrevious}>
                    <span dangerouslySetInnerHTML={{__html: iconChevronLeft}} />
                  </button>
                  <button className="btn btn--next btn-none" onClick={handleNext}>
                    <span dangerouslySetInnerHTML={{__html: iconChevronRight}} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="gwp__slider gwp__slider--single">{giftSlides}</div>
            )}
            {Boolean(claimedGifts.length) && giftVisibility && (
              <p className="gwp__remaining-spend h5 caption">
                {window.theme.strings.giftWithPurchase.oneGiftLimit ||
                  'Only one free gift can be added to cart. Remove one to add another.'}
              </p>
            )}

            {Boolean(excludedGifts.length && gifts.length) && giftVisibility && (
              <p className="gwp__remaining-spend h5 caption">
                {window.theme.strings.giftWithPurchase.additionalRemainingSpend.replace(
                  '{{ amount }}',
                  minRemainingSpend
                )}
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="gwp__banner caption">{gwpHeaderTitle()}</div>
      )}
    </div>
  );
};

GiftWithPurchase.propTypes = {
  gifts: PropTypes.arrayOf(PropTypes.object),
  excludedGifts: PropTypes.arrayOf(PropTypes.object),
  claimedGifts: PropTypes.arrayOf(PropTypes.object),
  remainingSpend: PropTypes.arrayOf(PropTypes.number),
  addToCart: PropTypes.func,
};

export default GiftWithPurchase;
